// Уникализация своих роликов: задания, загрузка файлов, отправка в чат.
// Контракт — backend/src/cf2/api/uniq.py.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSyncExternalStore } from 'react'
import { getToken } from '../auth/token'
import { setClosingConfirmation } from '../telegram'
import { ApiError, request } from './client'

export interface UniqLimits {
  max_file_bytes: number
  max_batch_bytes: number
  max_files: number
  max_copies: number
  default_copies: number
  extensions: string[]
}

export type UniqJobStatus = 'uploading' | 'queued' | 'processing' | 'done' | 'failed'
export type UniqFileStatus =
  | 'uploading'
  | 'uploaded'
  | 'queued'
  | 'waiting'
  | 'processing'
  | 'done'
  | 'failed'
  | 'skipped'

export interface UniqCopy {
  n: number
  size: number
  caption: string
  summary: string
  url: string
  download_url: string
}

export interface UniqFile {
  id: string
  name: string
  size: number
  uploaded: boolean
  status: UniqFileStatus
  stage: 'probe' | 'captions' | 'copies' | null
  progress: number
  error: string | null
  duration: number | null
  captions: 'codex' | 'transcript' | 'none' | null
  captions_note: string | null
  copies: UniqCopy[]
  zip_url: string | null
}

export interface UniqJob {
  id: string
  created_at: string
  expires_at: string
  copies: number
  status: UniqJobStatus
  queue_position: number | null
  files: UniqFile[]
}

export interface UniqOverview {
  jobs: UniqJob[]
  limits: UniqLimits
  captions_enabled: boolean
  send_enabled: boolean
}

const KEY = ['uniq'] as const

const uniqApi = {
  list: (signal?: AbortSignal) => request<UniqOverview>('/api/uniq/jobs', { signal }),
  create: (copies: number, files: File[]) =>
    request<UniqJob>('/api/uniq/jobs', {
      method: 'POST',
      body: { copies, files: files.map((f) => ({ name: f.name, size: f.size })) },
    }),
  start: (jobId: string) => request<UniqJob>(`/api/uniq/jobs/${jobId}/start`, { method: 'POST' }),
  remove: (jobId: string) => request<void>(`/api/uniq/jobs/${jobId}`, { method: 'DELETE' }),
  send: (jobId: string, fileId: string) =>
    request<{ sent: number; links: number }>(`/api/uniq/jobs/${jobId}/files/${fileId}/send`, { method: 'POST' }),
}

function isBusy(data: UniqOverview | undefined): boolean {
  return Boolean(data?.jobs.some((j) => j.status !== 'done' && j.status !== 'failed'))
}

/** Список заданий; пока что-то грузится или обрабатывается, обновляется раз в 2 с. */
export function useUniqJobs() {
  return useQuery({
    queryKey: KEY,
    queryFn: ({ signal }) => uniqApi.list(signal),
    refetchInterval: (query) => (isBusy(query.state.data) || hasActiveUploads() ? 2000 : 30_000),
    staleTime: 0,
  })
}

export function useDeleteUniqJob() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: uniqApi.remove,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export function useSendUniqToChat() {
  return useMutation({ mutationFn: ({ jobId, fileId }: { jobId: string; fileId: string }) => uniqApi.send(jobId, fileId) })
}

// Состояние загрузок живёт вне компонента: уход со страницы внутри приложения
// загрузку не обрывает, а при возвращении прогресс на месте.

export interface UploadState {
  loaded: number
  total: number
  error: string | null
  done: boolean
}

let uploads = new Map<string, UploadState>()
const listeners = new Set<() => void>()

function setUpload(key: string, patch: Partial<UploadState>): void {
  const prev = uploads.get(key) ?? { loaded: 0, total: 0, error: null, done: false }
  uploads = new Map(uploads).set(key, { ...prev, ...patch })
  guardClosing()
  listeners.forEach((cb) => cb())
}

// Пока файл грузится, случайный свайп вниз в Telegram или закрытие вкладки оборвёт
// загрузку гигабайта: Telegram спросит «Закрыть?», браузер — «Покинуть страницу?».
let guarding = false

function onBeforeUnload(e: BeforeUnloadEvent): void {
  e.preventDefault()
}

function guardClosing(): void {
  const active = hasActiveUploads()
  if (active === guarding) return
  guarding = active
  setClosingConfirmation(active)
  if (active) window.addEventListener('beforeunload', onBeforeUnload)
  else window.removeEventListener('beforeunload', onBeforeUnload)
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb)
  return () => listeners.delete(cb)
}

export function uploadKey(jobId: string, fileId: string): string {
  return `${jobId}/${fileId}`
}

function hasActiveUploads(): boolean {
  for (const u of uploads.values()) if (!u.done && !u.error) return true
  return false
}

export function useUploads(): Map<string, UploadState> {
  return useSyncExternalStore(subscribe, () => uploads)
}

function putFile(jobId: string, fileId: string, file: File): Promise<void> {
  const key = uploadKey(jobId, fileId)
  setUpload(key, { loaded: 0, total: file.size })
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', `/api/uniq/jobs/${jobId}/files/${fileId}`)
    const token = getToken()
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`)
    xhr.setRequestHeader('Content-Type', 'application/octet-stream')
    xhr.upload.onprogress = (e) => setUpload(key, { loaded: e.loaded, total: e.total || file.size })
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        setUpload(key, { loaded: file.size, done: true })
        resolve()
        return
      }
      let detail: string | null = null
      try {
        const data: unknown = JSON.parse(xhr.responseText)
        if (data && typeof data === 'object' && 'detail' in data && typeof data.detail === 'string') detail = data.detail
      } catch {
        // не JSON — оставляем статус
      }
      setUpload(key, { error: detail ?? `HTTP ${xhr.status}` })
      reject(new ApiError(xhr.status, detail))
    }
    xhr.onerror = () => {
      setUpload(key, { error: 'network' })
      reject(new ApiError(0, 'network'))
    }
    xhr.send(file)
  })
}

/**
 * Партия целиком: задание, файлы по одному (мобильный канал не делится на
 * несколько потоков), постановка в очередь. Не загрузился ни один — задание
 * удаляется. Возвращает, сколько файлов дошло.
 */
export async function uploadBatch(
  files: File[],
  copies: number,
  onChange: () => void,
): Promise<{ uploaded: number; failed: number }> {
  const job = await uniqApi.create(copies, files)
  onChange()
  let uploaded = 0
  for (const [i, meta] of job.files.entries()) {
    const file = files[i]
    if (!file) continue
    try {
      await putFile(job.id, meta.id, file)
      uploaded += 1
    } catch {
      // ошибка уже в состоянии загрузки; остальные файлы грузим дальше
    }
  }
  if (uploaded > 0) await uniqApi.start(job.id)
  else await uniqApi.remove(job.id).catch(() => undefined)
  onChange()
  return { uploaded, failed: job.files.length - uploaded }
}

export function useUniqRefresh(): () => void {
  const qc = useQueryClient()
  return () => void qc.invalidateQueries({ queryKey: KEY })
}


const ERRORS: Record<string, string> = {
  bad_type: 'Нужен MP4, MOV или WebM',
  file_too_large: 'Файл больше 1 ГБ',
  batch_too_large: 'За раз — не больше 5 ГБ',
  too_many_files: 'Слишком много файлов за раз',
  empty_file: 'Пустой файл',
  disk_full: 'На сервере мало места, попробуйте позже',
  bot_disabled: 'Бот выключен',
  bot_blocked: 'Бот не может написать вам: откройте чат с ботом и нажмите «Старт»',
  send_failed: 'Telegram не принял файлы',
  not_ready: 'Копии ещё не готовы',
  network: 'Нет связи',
  incomplete: 'Загрузка оборвалась',
  too_large: 'Файл больше, чем заявлен',
}

export function uniqError(err: unknown): string {
  const detail = err instanceof ApiError ? err.detail : typeof err === 'string' ? err : null
  return (detail && ERRORS[detail]) || 'Не получилось'
}
